import { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../api/api.config';

interface SquareConfig {
  applicationId: string;
  locationId: string;
  environment: string;
  isProduction: boolean;
  sdkUrl: string;
}

interface SquarePaymentHook {
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  tokenizeCard: () => Promise<{ status: string; token?: string; errors?: any[] }>;
  attachCard: (containerId: string) => Promise<void>;
  initializePayments: () => Promise<void>;
  cleanupCard: () => void;
}

declare global {
  interface Window {
    Square?: {
      payments: (applicationId: string, locationId: string) => {
        card: (options?: any) => Promise<{
          attach: (containerId: string) => Promise<void>;
          tokenize: () => Promise<{ status: string; token?: string; errors?: any[] }>;
        }>;
      };
    };
  }
}

export const useSquarePayments = (): SquarePaymentHook => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<SquareConfig | null>(null);
  const cardRef = useRef<any>(null);
  const paymentsRef = useRef<any>(null);
  const attachedContainers = useRef<Set<string>>(new Set());

  // Load Square configuration from backend
  const loadSquareConfig = async (): Promise<SquareConfig> => {
  // Use relative path so Vite dev proxy handles localhost:5001 without violating CSP
  const envParam = (import.meta as any)?.env?.DEV ? '?environment=sandbox' : '';
  const configUrl = getApiUrl(`squareconfig/frontend${envParam}`);
  const response = await fetch(configUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to load Square configuration: ${response.status}`);
    }
    
  return response.json();
  };

  // Load Square SDK dynamically
  const loadSquareSDK = async (sdkUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.Square) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = sdkUrl;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Square SDK'));
      
      document.head.appendChild(script);
    });
  };

  // Initialize Square Web Payments SDK
  const initializePayments = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Load configuration if not already loaded
      if (!config) {
        const squareConfig = await loadSquareConfig();
        setConfig(squareConfig);
        
        // Load SDK
        await loadSquareSDK(squareConfig.sdkUrl);
        
        if (!window.Square) {
          throw new Error('Square.js failed to load properly');
        }

        // Validate configuration
        if (!squareConfig.applicationId || !squareConfig.locationId) {
          throw new Error('Square configuration not available');
        }

        if (squareConfig.applicationId.includes('xxx') || 
            squareConfig.applicationId.includes('placeholder') ||
            squareConfig.applicationId.includes('NOT_CONFIGURED')) {
          throw new Error('Square payment system is not properly configured for development. Real Square sandbox credentials are required.');
        }

        // Initialize payments
        paymentsRef.current = window.Square.payments(squareConfig.applicationId, squareConfig.locationId);
        
        setIsReady(true);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Square payments';
      setError(errorMessage);
      console.error('Square initialization error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Attach card form to container
  const attachCard = async (containerId: string): Promise<void> => {
    if (!paymentsRef.current) {
      throw new Error('Square payments not initialized');
    }

    // Prevent multiple attachments to the same container
    if (attachedContainers.current.has(containerId)) {
      console.warn(`Card already attached to ${containerId}`);
      return;
    }

    const cardOptions = {
      style: {
        '.input-container': {
          borderColor: '#d1d5db',
          borderRadius: '8px'
        },
        '.input-container.is-focus': {
          borderColor: '#3b82f6'
        },
        '.input-container.is-error': {
          borderColor: '#ef4444'
        },
        '.message-text': {
          color: '#ef4444'
        },
        '.message-icon': {
          color: '#ef4444'
        }
      }
    };

    try {
      console.log('Creating Square card with options:', cardOptions);
      
      // Create card with longer timeout
      const cardInitTimeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Card initialization timeout - Square SDK took too long')), 15000);
      });
      
      const cardInitPromise = paymentsRef.current.card(cardOptions);
      cardRef.current = await Promise.race([cardInitPromise, cardInitTimeout]);
      
      console.log('Square card created, attempting to attach to:', containerId);
      
      // Attach card with longer timeout
      const attachTimeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Card attachment timeout - DOM element not ready')), 15000);
      });
      
      const attachPromise = cardRef.current.attach(containerId);
      await Promise.race([attachPromise, attachTimeout]);
      
      console.log('Square card attached successfully to:', containerId);
      
      // Mark this container as attached
      attachedContainers.current.add(containerId);
    } catch (error) {
      console.error('Square card attachment failed:', error);
      // Clean up on failure
      cardRef.current = null;
      throw error;
    }
  };

  // Tokenize card
  const tokenizeCard = async () => {
    if (!cardRef.current) {
      throw new Error('Card not initialized');
    }

    return cardRef.current.tokenize();
  };

  // Initialize on mount
  useEffect(() => {
    initializePayments();
  }, []);

  // Cleanup card attachments
  const cleanupCard = () => {
    cardRef.current = null;
    attachedContainers.current.clear();
  };

  return {
    isReady,
    isLoading,
    error,
    tokenizeCard,
    attachCard,
    initializePayments,
    cleanupCard
  };
};